// @flow
import React from 'react';
import PropTypes from 'prop-types';
import { QuantityInput } from 'moods-react/QuantityInput';
import { Notification, NotificationType } from 'moods-react/Notification';
import { Button, buttonSizes, buttonTypes } from 'moods-react/Button';
import { SelectDropdown } from '../../../components/SelectDropdown';
import { getProductTypeIntlPluralKey } from '../../services/product-type/ProductTypeService';
import Breadcrumbs from '../../common/breadcrumbs/BreadcrumbsComponent';
import ImageLoader from '../../common/image-loader/ImageLoaderComponent';
import { envUrl } from '../../services/environment/EnvironmentService';
import { getRouteFromId } from '../../data/AccessoriesNavItems';
import accessoriesMessages from '../../../data/accessories';
import { canOrderAccessories } from '../../services/user-permission/UserPermissionService';
import AccessoriesRestrictedView from '../AccessoriesRestrictedViewComponent';
import ProductUnavailableComponent from '../../products/product/ProductUnavailable';
import CustomisableMessage from './CustomisableMessageComponent';

import './AccessoryPage.scss';

type Props = {
  actions: {
    getAccessory: Function,
    accessoryCartRequest: Function,
    addedAccessoryToCart: Function,
    accessoryQuantityChange: Function,
    accessoryVariantChange: Function,
  },
  location: {
    pathname: string,
  },
  accessories: Object,
};

class AccessoryPage extends React.Component<Props> {
  static contextTypes = {
    intl: PropTypes.shape({
      formatMessage: PropTypes.func.isRequired,
    }),
  };

  constructor() {
    super();
    this.state = {
      selectedCriteria: [],
      selectedVariant: null,
    };
  }

  componentDidUpdate(prevProps: Props): void {
    const { specification } = this.props.accessories.activeItem;
    if (specification && specification !== prevProps.accessories.activeItem.specification) {
      specification
        .filter((spec) => spec.values.length === 1)
        .map((spec) => this.setCriteria(spec.label, spec.values[0], true));
    }
  }

  UNSAFE_componentWillMount(): void {
    const productType = this.getProductType();
    this.props.actions.getAccessory(productType);
    window.scrollTo(0, 0);
  }

  UNSAFE_componentWillReceiveProps(nextProps: { accessories: { activeItem: { isAdded: boolean } } }) {
    if (!this.props.accessories.activeItem.isAdded && nextProps.accessories.activeItem.isAdded) {
      setTimeout(() => {
        this.props.actions.addedAccessoryToCart(false);
      }, 1000);
    }
  }

  getProductType(): string {
    return this.props.location.pathname.split('/').pop();
  }

  setCriteria = (criteriaName: string, criteriaLabel: string, singleOptionCriterion: boolean): void => {
    if (criteriaLabel === null) return;
    this.props.actions.accessoryVariantChange({
      label: criteriaName,
      value: criteriaLabel,
      singleOptionCriterion,
    });
  };

  handleAddToCartClick = () => {
    const { actions, accessories } = this.props;
    const { canAddToCart, cartQuantity, selectedVariant } = accessories.activeItem;

    if (canAddToCart) {
      actions.accessoryCartRequest(selectedVariant.name, cartQuantity);
    }
  };

  getCurrencyValue = (currencyAmount: string) => {
    return Number(currencyAmount.replace(/[^0-9.-]+/g, ''));
  };

  getVariationDropdown = () => {
    const { specification, selectedCriteria } = this.props.accessories.activeItem;

    return specification.map((spec, index) => {
      const key = spec.label + index;
      const selectedValue = selectedCriteria.find((criterion) => criterion.label === spec.label);

      let accessorySelector;
      if (spec.values.length === 1) {
        [accessorySelector] = spec.values;
      } else {
        accessorySelector = (
          <SelectDropdown
            placeholder={`Choose a ${spec.label.toLowerCase()}`}
            values={spec.values.map((value) => [value, value])}
            onChange={(elem) => this.setCriteria(spec.label, elem, false)}
            defaultValue={selectedValue && selectedValue.value}
          />
        );
      }

      return (
        <div className="accessory-page__field" key={key}>
          <div className="accessory-page__field-label">{spec.label}:</div>
          <div className="accessory-page__field-input">{accessorySelector}</div>
        </div>
      );
    });
  };

  getQuantityPicker = () => {
    const {
      price,
      quantity,
      bundleSize,
      canAddToCart,
      outOfStock,
      selectedAllCriteria,
    } = this.props.accessories.activeItem;

    return (
      <div className="accessory-page__field accessory-page__field--has-message-panel">
        <div className="accessory-page__field-label">
          {this.context.intl.formatMessage(accessoriesMessages.quantityLabel)}:
        </div>
        {outOfStock && selectedAllCriteria ? (
          <div className="accessory-page__product-out-of-stock accessory-page__field-input">
            <Notification type={NotificationType.Error} icon visible size="small">
              <p className="notification__text">
                {this.context.intl.formatMessage(accessoriesMessages.outOfStockMessage)}
              </p>
            </Notification>
          </div>
        ) : (
          <div className="accessory-page__field-input">
            <QuantityInput
              quantity={quantity}
              minQuantity={bundleSize}
              onChange={this.props.actions.accessoryQuantityChange}
              quantityIncrement={bundleSize}
              disabled={!canAddToCart}
              successMessage={
                price.bulkDiscount && this.getCurrencyValue(price.bulkDiscount) !== 0
                  ? `${this.context.intl.formatMessage(accessoriesMessages.accessoriesSaving)} ${price.bulkDiscount}`
                  : ''
              }
            />
          </div>
        )}
      </div>
    );
  };

  getPriceAndAddToCartButton = () => {
    const { price, isAdding, isAdded, canAddToCart } = this.props.accessories.activeItem;

    return (
      <div className="accessory-page__add-to-cart">
        <div className="accessory-page__price-container">
          <span className="accessory-page__price">{price.discounted ? price.discounted : price.original}</span>
          {price.discounted && <span className="accessory-page__strike-through-price">{price.original}</span>}
        </div>
        <div className="accessory-page__add-to-cart-button-container">
          <Button
            data-add-to-cart
            className="accessory-page__add-to-cart-button"
            type={buttonTypes.PRIMARY}
            size={buttonSizes.SMALL}
            isLoading={isAdding}
            isSuccess={isAdded}
            isDisabled={!canAddToCart}
            onClick={this.handleAddToCartClick}
          >
            {this.context.intl.formatMessage(accessoriesMessages.addToCartButtonText)}
          </Button>
        </div>
      </div>
    );
  };

  getBreadcrumbs = () => {
    const { title, category } = this.props.accessories.activeItem;
    const categoryRoute = getRouteFromId(category);
    const currentCategoryPage = `/accessories/${categoryRoute}`;

    return (
      <Breadcrumbs
        className="accessory-page__breadcrumbs"
        items={[
          {
            name: 'Accessories',
            url: `${envUrl('/accessories')}`,
          },
          {
            name: this.context.intl.formatMessage(getProductTypeIntlPluralKey(category)),
            url: `${envUrl(currentCategoryPage)}`,
          },
          {
            name: title || '',
            url: '#',
          },
        ]}
      />
    );
  };

  getProductDescription = () => {
    const { longDescriptionBody, longDescriptionTitle } = this.props.accessories.activeItem;
    return (
      <div className="accessory-page__long-description">
        <div className="accessory-page__long-description-title">{longDescriptionTitle}</div>
        {/* eslint-disable */}
        <div
          className="accessory-page__long-description-text"
          dangerouslySetInnerHTML={{ __html: longDescriptionBody }}
        />
        {/* eslint-enable */}
      </div>
    );
  };

  getProductDetails = () => {
    const { actions } = this.props;
    const {
      productUnavailable,
      availability,
      isCustomisable,
      customiseAccessoryUrl,
      title,
    } = this.props.accessories.activeItem;
    if (productUnavailable) {
      return (
        <div className="accessory-page__product-unavailable">
          <Notification type={NotificationType.Error} icon visible>
            <ProductUnavailableComponent availability={availability} />
          </Notification>
        </div>
      );
    }

    return (
      <div className="accessory-page__content">
        <div>
          {this.getVariationDropdown()}
          {this.getQuantityPicker()}
          {isCustomisable && (
            <CustomisableMessage
              mobile
              formUrl={customiseAccessoryUrl}
              email={this.props.config.email}
              productName={title}
              openCustomiseRequestForm={actions.openCustomiseRequestForm}
              closeCustomiseRequestForm={actions.closeCustomiseRequestForm}
            />
          )}
        </div>
        {this.getProductDescription()}
      </div>
    );
  };

  getOverlayText = () => {
    const { productUnavailable, outOfStock, selectedAllCriteria } = this.props.accessories.activeItem;

    if (productUnavailable || (outOfStock && selectedAllCriteria)) {
      return this.context.intl.formatMessage(accessoriesMessages.productUnavailableOverlayText);
    }

    return null;
  };

  render() {
    const { config, actions, accessories } = this.props;
    const {
      isLoading,
      title,
      category,
      images,
      productUnavailable,
      isCustomisable,
      customiseAccessoryUrl,
    } = accessories.activeItem;

    if (isLoading) {
      return <div className="accessory-page__loading" />;
    }

    if (!canOrderAccessories(config.permissions)) {
      return <AccessoriesRestrictedView actions={actions} config={config} />;
    }

    return (
      <div className="accessory-page">
        <div className="accessory-page__image-panel">
          <div className="accessory-page__breadcrumbs-container--desktop">{this.getBreadcrumbs()}</div>
          <div className="accessory-page__image-container">
            <ImageLoader
              image={images.preview}
              altText="title"
              type="square"
              overlayText={this.getOverlayText()}
              noBackground
              isAccessories
              extraPadding={isCustomisable}
            />
            {isCustomisable && (
              <CustomisableMessage
                desktop
                email={config.email}
                formUrl={customiseAccessoryUrl}
                productName={title}
                openCustomiseRequestForm={actions.openCustomiseRequestForm}
                closeCustomiseRequestForm={actions.closeCustomiseRequestForm}
              />
            )}
          </div>
        </div>
        <div className="accessory-page__details-panel">
          <div className="accessory-page__breadcrumbs-container--mobile">{this.getBreadcrumbs()}</div>
          <h3 className="accessory-page__name">{title}</h3>
          <div className="accessory-page__category">
            {this.context.intl.formatMessage(getProductTypeIntlPluralKey(category))}
          </div>
          {this.getProductDetails()}
        </div>
        {!productUnavailable && this.getPriceAndAddToCartButton()}
      </div>
    );
  }
}

export default AccessoryPage;
